import React, { useContext } from 'react'
import { ThemeContext } from 'styled-components'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { minAvisosEnFacetas } from '@navent-jobs/interlinking'

// ui-kit
import { Col, Container, Row } from '@navent-jobs/ui-kit/components/grid'
import Text from '@navent-jobs/ui-kit/components/text'

// mixins
import { AdditionalInfoWrapper, CompanyInfo, IconInfo, CustomIcon, InformationList } from './mixins'
import { InformationItem, LinkItem, InformationlIcon, InfoLabel } from '../common/mixins'

import { IS_ZONA_JOBS, SITE_ID } from '../../../../../../../constants'
import { capitalize } from '../../../../../../../constants/commons/caitalize'
import features from '../../../../../../../constants/features-per-country'

const NO_ESPECIFICADO = 'No especificado'

const searchAvisosV2Enabled = features[SITE_ID || 'BMAR'].searchAvisosV2.enabled

const createSalarioString = (low, high, thousandSeparator, moneySymbol) => {
  const replaceCurrencySimbols = inputCurrency => {
    if (!inputCurrency) {
      return
    }

    return `${moneySymbol}${inputCurrency.replace(/\D+/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)}`
  }

  if (!low && !high) {
    return NO_ESPECIFICADO
  }

  if (!low) {
    return `Hasta ${replaceCurrencySimbols(high)}`
  }

  if (!high) {
    return `Desde ${replaceCurrencySimbols(low)}`
  }

  const result = low > 0 ? `De ${replaceCurrencySimbols(low)} a ${replaceCurrencySimbols(high)}` : ''
  return result
}

interface Salario {
  excluyente: boolean
  frecuenciaPago: {
    id: number
    nombre: string
  }
  salarioMaximo: number
  salarioMinimo: number
  solicitarCandidato: boolean
  tipo: string
}

const getSalario = (salario: Salario, t, colors) => {
  if (!salario || isEmpty(salario) || !salario.tipo) {
    return null
  }

  const thousandSeparator = t(`currencyMask.thousandsSeparatorSymbol`) || '.'
  const moneySymbol = t(`currencyMask.symbol`) || '$'
  const { salarioMinimo, salarioMaximo, frecuenciaPago } = salario
  const low = salarioMinimo ? `${salarioMinimo}` : null
  const high = salarioMaximo ? `${salarioMaximo}` : null
  const rango = createSalarioString(low, high, thousandSeparator, moneySymbol)
  const frecuencia = frecuenciaPago ? ` ${frecuenciaPago.nombre.toLowerCase()}` : ''

  return (
    <InformationItem>
      <InformationlIcon name="icon-light-money" color={colors.secondary.normal} size="20" />
      <Text size="md" variant="gray60">
        {capitalize(`${rango}${frecuencia}`.trim())}
      </Text>
    </InformationItem>
  )
}

const getAptoDiscapacitado = (aviso, urlMaker, iconOptions, linkOptions) => {
  return aviso?.aptoDiscapacitados ? (
    <InformationItem>
      <LinkItem
        href={`${
          urlMaker &&
          urlMaker({ apto_discapacitado: searchAvisosV2Enabled ? 'discapacitados-apto' : 'apto-discapacitado' })
        }`}
        disabled={!urlMaker}
        icon={{ name: 'icon-light-discapacity', ...iconOptions }}
        linkoptions={linkOptions}
      >
        <Text size="md" variant="gray60">
          Apto personas con discapacidad
        </Text>
      </LinkItem>
    </InformationItem>
  ) : null
}

const DetalleAviso = ({ aviso, urlMaker }) => {
  const { colors } = useContext<{ colors }>(ThemeContext)
  const { t } = useTranslation()
  const noFollowSubarea = aviso?.subArea.cantidadAvisos < minAvisosEnFacetas
  const noFollowModalidadTrabajo = aviso?.modalidadTrabajo.cantidadAvisos < minAvisosEnFacetas
  const inclusiveCompany = aviso?.empresa.inclusiva
  const tipoTrabajo = aviso?.tipoTrabajo?.nombre
  const tipoContratacion = aviso?.tipoContratacion?.nombre
  const tieneNivelLaboral = !isEmpty(aviso?.nivelLaboral?.nombre)
  const showThirdCol = tieneNivelLaboral && aviso?.requisitos?.detalleSalario?.tipo && aviso?.cantidadVacantes

  const linkOptions = {
    native: !IS_ZONA_JOBS,
    size: 'medium',
    type: 'link',
    variant: 'secondary',
    block: true,
  }
  const iconOptions = {
    color: colors.secondary.normal,
    position: 'left',
  }

  return (
    <Container noGutter>
      <Row noGutter>
        <InformationList>
          <Col noGutter>
            {/** Modalidad de trabajo * */}
            {aviso?.modalidadTrabajo && (
              <InformationItem>
                <LinkItem
                  href={`${
                    urlMaker &&
                    urlMaker({
                      modalidad_trabajo: searchAvisosV2Enabled
                        ? aviso?.modalidadTrabajo.idSemantico
                        : aviso?.modalidadTrabajo.stringId,
                    })
                  }`}
                  disabled={!urlMaker}
                  icon={{ name: 'icon-light-office', ...iconOptions }}
                  linkoptions={linkOptions}
                  noFollow={noFollowModalidadTrabajo}
                  {...(noFollowModalidadTrabajo && {
                    link: `${
                      urlMaker &&
                      urlMaker({
                        modalidad_trabajo: searchAvisosV2Enabled
                          ? aviso?.modalidadTrabajo.idSemantico
                          : aviso?.modalidadTrabajo.stringId,
                      })
                    }`,
                  })}
                >
                  <Text size="md" variant="gray60">
                    {aviso?.modalidadTrabajo.nombre}
                  </Text>
                </LinkItem>
              </InformationItem>
            )}

            {/** Subarea * */}
            {!isEmpty(aviso?.subArea.nombre) && (
              <InformationItem>
                <LinkItem
                  href={`${
                    urlMaker &&
                    urlMaker({ subarea: searchAvisosV2Enabled ? aviso?.subArea.idSemantico : aviso?.subArea.stringId })
                  }`}
                  disabled={aviso?.origenAviso === 'ZONAJOBS' || !urlMaker}
                  icon={{ name: 'icon-light-cube', ...iconOptions }}
                  linkoptions={linkOptions}
                  noFollow={noFollowSubarea}
                  {...(noFollowSubarea && {
                    link: `${
                      urlMaker &&
                      urlMaker({
                        subarea: searchAvisosV2Enabled ? aviso?.subArea.idSemantico : aviso?.subArea.stringId,
                      })
                    }`,
                  })}
                >
                  <Text size="md" variant="gray60">
                    {aviso?.subArea.nombre}
                  </Text>
                </LinkItem>
              </InformationItem>
            )}

            {/** Tipo de trabajo y contratacion * */}
            {(!isEmpty(tipoTrabajo) || !isEmpty(tipoContratacion)) && (
              <InformationItem>
                <CustomIcon name="icon-light-clock" size="20" color={colors.secondary.normal} />
                <Text size="md" variant="gray60">
                  {tipoTrabajo}
                  {tipoTrabajo && tipoContratacion ? ', ' : ''}
                  {tipoContratacion}
                </Text>
              </InformationItem>
            )}
          </Col>
          <Col noGutter>
            {/** Nivel laboral * */}
            {tieneNivelLaboral ? (
              <InformationItem>
                <LinkItem
                  href={`${
                    urlMaker &&
                    urlMaker({
                      nivel_laboral: searchAvisosV2Enabled
                        ? aviso?.nivelLaboral.idSemantico
                        : aviso?.nivelLaboral.stringId,
                    })
                  }`}
                  disabled={aviso?.origenAviso === 'ZONAJOBS' || !urlMaker}
                  icon={{ name: 'icon-light-award', ...iconOptions }}
                  linkoptions={linkOptions}
                >
                  <Text size="md" variant="gray60">
                    {aviso?.nivelLaboral.nombre}
                  </Text>
                </LinkItem>
              </InformationItem>
            ) : (
              <InformationItem>
                <InformationlIcon name="icon-light-award" color={colors.secondary.normal} size="20" />
                <Text size="md" variant="gray60">
                  {NO_ESPECIFICADO}
                </Text>
              </InformationItem>
            )}

            {/** Salario * */}
            {!isEmpty(aviso?.requisitos) && getSalario(aviso.requisitos?.detalleSalario, t, colors)}

            {/** Cantidad vacantes * */}
            {aviso?.cantidadVacantes && (
              <InformationItem>
                <CustomIcon name="icon-light-people" size="20" color={colors.secondary.normal} />
                <Text size="md" variant="gray60">
                  {aviso?.cantidadVacantes}{' '}
                  {aviso?.cantidadVacantes === 1 ? 'vacante disponible' : 'vacantes disponibles'}
                </Text>
              </InformationItem>
            )}

            {/** Apto discapacitados * */}
            {!showThirdCol && getAptoDiscapacitado(aviso, urlMaker, iconOptions, linkOptions)}
          </Col>
          <Col noGutter>
            {/** Apto discapacitados * */}
            {showThirdCol && getAptoDiscapacitado(aviso, urlMaker, iconOptions, linkOptions)}
          </Col>
        </InformationList>
      </Row>
      {inclusiveCompany && (
        <AdditionalInfoWrapper>
          <CompanyInfo>
            <IconInfo name="icon-light-info" size="20" color={colors.secondary.normal} />
            <Text size="sm" variant="secondary">
              {t('fichaAviso.sections.details.empresaInclusiva', { cv: t('cv') })}
            </Text>
          </CompanyInfo>
        </AdditionalInfoWrapper>
      )}
    </Container>
  )
}

export default DetalleAviso
